import React, { useReducer, useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Play, Pause, CheckCircle2, XCircle,
  Loader2, AlertCircle, Building2, ExternalLink, Info,
} from 'lucide-react';

import { Button, Card } from '../../ui-v2';
import { Badge, Select } from '../../ui-v2.5';
import {
  wizardReducer, initialState, buildPayload, duracionValida, DURACION_MIN, DURACION_MAX,
} from '../wizard/wizardState';
import {
  SECCIONES_EDITAR, erroresPorSeccion, erroresSinSeccion, firmaPayload, hayCambios,
  transicionesDe, debeAvisarAlSalir, TEXTO_SALIR_SIN_GUARDAR,
} from '../wizard/editarSecciones';
import {
  getCampanaById, updateCampana, cambiarEstadoCampana, previewImpacto,
} from '../api';
import { ESTADO_CAMPANA, etiquetaDe } from '../compartidos/etiquetasEstado';

import Paso2Subtareas from '../wizard/Paso2Subtareas';
import Paso3Fechas from '../wizard/Paso3Fechas';
import Paso4DiasDisponibles from '../wizard/Paso4DiasDisponibles';
import Paso5Tecnicos from '../wizard/Paso5Tecnicos';
import { InformacionDosColumnas, BloqueReglas } from '../wizard/BloquesCampana';
import UsuariosEnLotes from '../lotes/UsuariosEnLotes';
import ModalImpacto from '../components/ModalImpacto';

/**
 * EditarCampana — rediseño (Fase 2): navegación lateral por secciones, los mismos componentes del
 * asistente y una barra fija abajo para guardar.
 *
 * Lo que NO cambia: la carga (Borrador → asistente; Completada o Cancelada → error), el guardado
 * (previewImpacto → ModalImpacto → updateCampana), las transiciones con su modal, el envío de
 * excluidos_manuales con su marca excluidos_cargados (lo hace el reducer en LOAD) y la puerta única de
 * personas en curso (UsuariosEnLotes, solo lectura). La ruta solo se abre con campanas.manage (App.js).
 *
 * Cambios sin guardar: se comparan con la huella del payload cargado. Se avisa al cerrar o recargar
 * la pestaña, al pulsar un enlace de la intranet (menú lateral) y en las salidas de esta pantalla.
 * No se puede interceptar el botón «atrás» del navegador ni lo que navega con navigate() fuera de
 * esta pantalla (BrowserRouter no tiene bloqueo de navegación).
 */
export default function EditarCampana() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);

  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [campanaOriginal, setCampanaOriginal] = useState(null);
  const [firmaBase, setFirmaBase] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargaError, setCargaError] = useState(null);

  const [guardando, setGuardando] = useState(false);
  const [errorGuardado, setErrorGuardado] = useState(null);
  const [mensajeOk, setMensajeOk] = useState(null);

  const [impactoModal, setImpactoModal] = useState(null);
  const [transicionModal, setTransicionModal] = useState(null);
  const [transicionLoading, setTransicionLoading] = useState(false);

  const [activa, setActiva] = useState(SECCIONES_EDITAR[0].id);

  // Carga en el estado y guarda la huella de lo cargado (lo que cuenta como «sin cambios»).
  function cargarEnFormulario(c) {
    setCampanaOriginal(c);
    dispatch({ type: 'LOAD', campana: c });
    setFirmaBase(firmaPayload(wizardReducer(initialState, { type: 'LOAD', campana: c }).payload));
  }

  // --- Carga inicial ---
  useEffect(() => {
    if (!id) return;
    setCargando(true);
    setCargaError(null);
    getCampanaById(id)
      .then((c) => {
        if (c.estado === 'Borrador') {
          navigate(`/citas-tecnicas/campanas/${c.id}/wizard`, { replace: true });
          return;
        }
        if (c.estado === 'Completada' || c.estado === 'Cancelada') {
          setCargaError(`Esta campaña está ${c.estado.toLowerCase()} y no puede editarse.`);
          return;
        }
        cargarEnFormulario(c);
      })
      .catch((e) => {
        console.error('[EditarCampana]', e);
        setCargaError(
          e?.response?.status === 404 ? 'La campaña no existe.' : 'No se pudo cargar la campaña.'
        );
      })
      .finally(() => setCargando(false));
  }, [id, navigate]);

  const sucio = hayCambios(state.payload, firmaBase);
  const duracionOk = duracionValida(state.payload.duracion_cita);

  // --- Avisos al salir con cambios sin guardar ---
  const sucioRef = useRef(sucio);
  sucioRef.current = sucio;

  useEffect(() => {
    if (!sucio) return undefined;
    const antesDeDescargar = (e) => { e.preventDefault(); e.returnValue = ''; };
    // Captura en document: corre antes que el manejador de <Link>/<NavLink> (React escucha en la raíz).
    const alPulsar = (e) => {
      const a = e.target?.closest?.('a[href]');
      if (!a) return;
      const avisar = debeAvisarAlSalir({
        sucio: sucioRef.current,
        href: a.href,
        origen: window.location.origin,
        rutaActual: `${window.location.pathname}${window.location.search}`,
        target: a.getAttribute('target') || '',
        descarga: a.hasAttribute('download'),
        boton: e.button,
        modificador: e.metaKey || e.ctrlKey || e.shiftKey || e.altKey,
      });
      if (avisar && !window.confirm(TEXTO_SALIR_SIN_GUARDAR)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', antesDeDescargar);
    document.addEventListener('click', alPulsar, true);
    return () => {
      window.removeEventListener('beforeunload', antesDeDescargar);
      document.removeEventListener('click', alPulsar, true);
    };
  }, [sucio]);

  function salirA(ruta) {
    if (sucioRef.current && !window.confirm(TEXTO_SALIR_SIN_GUARDAR)) return;
    navigate(ruta);
  }

  // --- Sección visible (navegación lateral) ---
  useEffect(() => {
    if (!campanaOriginal || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver((entradas) => {
      const visibles = entradas.filter((x) => x.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visibles.length) setActiva(visibles[0].target.id.replace(/^sec-/, ''));
    }, { rootMargin: '-120px 0px -55% 0px' });
    SECCIONES_EDITAR.forEach((s) => {
      const el = document.getElementById(`sec-${s.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [campanaOriginal]);

  function irA(seccion) {
    setActiva(seccion);
    document.getElementById(`sec-${seccion}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // --- Guardar cambios (mismo flujo de siempre) ---
  async function intentarGuardar() {
    setErrorGuardado(null);
    setMensajeOk(null);
    if (!duracionOk) {
      setErrorGuardado(`La duración por cita debe ser un número entero entre ${DURACION_MIN} y ${DURACION_MAX} minutos.`);
      irA('fechas');
      return;
    }
    setGuardando(true);
    try {
      const payload = buildPayload(state, {});
      const impacto = await previewImpacto(id, payload);
      if (impacto.tiene_impacto) {
        setImpactoModal(impacto);
        setGuardando(false);
        return;
      }
      await ejecutarGuardado(payload, false);
    } catch (e) {
      console.error('[intentarGuardar]', e);
      setErrorGuardado(e?.response?.data?.error || 'No se pudo evaluar el impacto.');
      setGuardando(false);
    }
  }

  async function ejecutarGuardado(payload, confirmarImpacto) {
    setGuardando(true);
    setErrorGuardado(null);
    try {
      await updateCampana(id, { ...payload, confirmar_impacto: confirmarImpacto });
      const c = await getCampanaById(id);
      cargarEnFormulario(c);
      setImpactoModal(null);
      setMensajeOk('Guardado');
      setTimeout(() => setMensajeOk(null), 2500);
    } catch (e) {
      console.error('[ejecutarGuardado]', e);
      const errs = e?.response?.data?.errores;
      if (Array.isArray(errs) && errs.length) {
        dispatch({ type: 'SET_ERRORES', errores: errs });
        setImpactoModal(null);
        setErrorGuardado('Hay errores de validación. Revisa las secciones marcadas.');
      } else {
        setErrorGuardado(e?.response?.data?.error || 'No se pudo guardar la campaña.');
      }
    } finally {
      setGuardando(false);
    }
  }

  function confirmarImpacto() {
    return ejecutarGuardado(buildPayload(state, {}), true);
  }

  function descartar() {
    if (!campanaOriginal) return;
    if (sucio && !window.confirm('¿Descartar los cambios sin guardar?')) return;
    cargarEnFormulario(campanaOriginal);
    setErrorGuardado(null);
    setMensajeOk(null);
  }

  // --- Transición de estado (mismo modal de siempre) ---
  async function ejecutarTransicion() {
    if (!transicionModal) return;
    setTransicionLoading(true);
    try {
      await cambiarEstadoCampana(id, transicionModal.nuevoEstado);
      const c = await getCampanaById(id);
      if (c.estado === 'Completada' || c.estado === 'Cancelada') {
        sucioRef.current = false; // la campaña ya no se edita: nada que avisar
        navigate('/citas-tecnicas/campanas');
        return;
      }
      cargarEnFormulario(c);
      setTransicionModal(null);
    } catch (e) {
      console.error('[ejecutarTransicion]', e);
      const msg = e?.response?.data?.error || 'No se pudo cambiar el estado.';
      setTransicionModal((m) => ({ ...m, error: msg }));
    } finally {
      setTransicionLoading(false);
    }
  }

  const porSeccion = useMemo(() => erroresPorSeccion(state.errores), [state.errores]);
  const sueltos = useMemo(() => erroresSinSeccion(state.errores), [state.errores]);

  // --- Render ---
  if (cargando) {
    return (
      <div className="px-6 py-12 text-center text-slate-500 text-sm">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando campaña…
      </div>
    );
  }
  if (cargaError) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {cargaError}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/campanas')}>
            <ArrowLeft className="w-4 h-4" />
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }
  if (!campanaOriginal) return null;

  const estado = etiquetaDe(ESTADO_CAMPANA, campanaOriginal.estado);
  const colab = campanaOriginal.ambito === 'colaborador';
  const transiciones = transicionesDe(campanaOriginal.estado);
  const ocupado = guardando || transicionLoading;

  // El ámbito no se cambia en edición: Paso1Info no pinta su selector (sinAmbito) y, por si acaso,
  // la acción no llega al reducer.
  const dispatchSinAmbito = (action) => {
    if (action.type === 'CAMBIAR_AMBITO') return;
    dispatch(action);
  };

  const contenido = {
    informacion: <InformacionDosColumnas payload={state.payload} errores={state.errores} dispatch={dispatchSinAmbito} />,
    subtareas: <Paso2Subtareas payload={state.payload} errores={state.errores} dispatch={dispatch} />,
    reglas: <BloqueReglas payload={state.payload} errores={state.errores} dispatch={dispatchSinAmbito} />,
    fechas: (
      <Paso3Fechas
        payload={state.payload}
        errores={state.errores}
        dispatch={dispatch}
        campanaId={id}
        onIrA={salirA}
        sinRecordatorios
      />
    ),
    dias: <Paso4DiasDisponibles payload={state.payload} dispatch={dispatch} />,
    // Las citas de cada técnico en esta campaña no llegan en getCampanaById: se enseñan sin ellas.
    tecnicos: <Paso5Tecnicos dosColumnas payload={state.payload} errores={state.errores} dispatch={dispatch} />,
    // Lotes: en curso, una sola puerta para las personas, la pestaña Personas del resumen. Aquí, solo
    // lectura. El servidor rechaza (422) otra lista; la que se reenvía al guardar es la misma.
    personas: (
      <UsuariosEnLotes
        campanaId={id}
        onIrAlPanel={() => salirA(`/citas-tecnicas/campanas/${id}/resumen?tab=personas`)}
      />
    ),
  };

  return (
    <div className="min-h-full bg-v25-surface-sunken">
      {/* Cabecera */}
      <div className="border-b border-v25-line bg-white">
        <div className="px-4 pt-4 sm:px-6">
          <button
            type="button"
            onClick={() => salirA('/citas-tecnicas/campanas')}
            className="mb-2 inline-flex items-center gap-1 text-xs text-v25-ink-500 hover:text-v25-ink-900"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Campañas
          </button>
          <div className="flex flex-col gap-3 pb-4 md:flex-row md:items-start md:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold text-v25-ink-900 break-words">{campanaOriginal.titulo}</h1>
                <Badge tone={estado.tone}>{estado.label}</Badge>
                <Badge tone="info" icon={colab ? ExternalLink : Building2}>{colab ? 'Colaboradores' : 'Internos'}</Badge>
              </div>
              <p className="mt-1 text-sm text-v25-ink-500">
                Editar campaña · los cambios no se aplican hasta que guardes.
              </p>
            </div>
            {transiciones.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {transiciones.map((t) => {
                  const Icono = ICONO_TRANSICION[t.estado];
                  return (
                    <Button
                      key={t.estado}
                      type="button"
                      variant="outline"
                      size="sm"
                      className={t.peligro ? 'text-red-700' : undefined}
                      onClick={() => setTransicionModal({ nuevoEstado: t.estado })}
                      disabled={ocupado}
                    >
                      {Icono && <Icono className="h-4 w-4" />}
                      {t.label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* Navegación: lateral fija en escritorio, «Ir a» en móvil */}
        <nav aria-label="Secciones" className="lg:sticky lg:top-20 lg:h-fit">
          <div className="lg:hidden">
            <Select label="Ir a" value={activa} onChange={(e) => irA(e.target.value)}>
              {SECCIONES_EDITAR.map((s) => (
                <option key={s.id} value={s.id}>{s.titulo}{porSeccion[s.id] ? ' (con errores)' : ''}</option>
              ))}
            </Select>
          </div>
          <ul className="hidden space-y-0.5 lg:block">
            {SECCIONES_EDITAR.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => irA(s.id)}
                  aria-current={activa === s.id ? 'true' : undefined}
                  className={`flex w-full items-center justify-between gap-2 rounded-v25-md px-3 py-2 text-left text-sm ${activa === s.id ? 'bg-white font-medium text-v25-ink-900 shadow-sm ring-1 ring-v25-line' : 'text-v25-ink-500 hover:bg-white/60 hover:text-v25-ink-900'}`}
                >
                  <span>{s.titulo}</span>
                  {porSeccion[s.id] > 0 && (
                    <span className="rounded-full bg-red-100 px-1.5 text-[11px] font-medium tabular-nums text-red-700" title="Errores en esta sección">
                      {porSeccion[s.id]}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 space-y-5 pb-6">
          {SECCIONES_EDITAR.map((s) => (
            <Card key={s.id} id={`sec-${s.id}`} className="scroll-mt-24 p-5">
              <div className="mb-4">
                <h2 className="flex items-center gap-2 text-base font-semibold text-v25-ink-900">
                  {s.titulo}
                  {porSeccion[s.id] > 0 && <AlertCircle className="h-4 w-4 text-red-600" aria-label="Con errores" />}
                </h2>
                <p className="text-xs text-v25-ink-500">{s.desc}</p>
              </div>
              {contenido[s.id]}
            </Card>
          ))}
        </div>
      </div>

      {/* Barra de guardar, fija abajo */}
      <div className="sticky bottom-0 z-20 border-t border-v25-line bg-white/95 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1 text-xs">
            {errorGuardado ? (
              <div className="text-red-700">
                <span className="flex items-center gap-1.5"><AlertCircle className="h-3.5 w-3.5 shrink-0" />{errorGuardado}</span>
                {sueltos.length > 0 && (
                  <ul className="mt-1 list-disc pl-6">
                    {sueltos.map((e, i) => <li key={i}>{e.mensaje || e.campo}</li>)}
                  </ul>
                )}
              </div>
            ) : mensajeOk ? (
              <span className="flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />{mensajeOk}</span>
            ) : sucio ? (
              <span className="flex items-center gap-1.5 text-amber-700"><AlertCircle className="h-3.5 w-3.5" />Cambios sin guardar.</span>
            ) : (
              <span className="flex items-center gap-1.5 text-v25-ink-500"><Info className="h-3.5 w-3.5" />Si el cambio afecta a citas ya reservadas, te lo enseñará antes de guardar.</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={descartar} disabled={!sucio || ocupado}>Descartar</Button>
            <Button type="button" onClick={intentarGuardar} disabled={!sucio || ocupado}>
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar cambios
            </Button>
          </div>
        </div>
      </div>

      <ModalImpacto
        abierto={!!impactoModal}
        impacto={impactoModal}
        guardando={guardando}
        onCancelar={() => setImpactoModal(null)}
        onConfirmar={confirmarImpacto}
      />

      {transicionModal && (
        <ModalTransicion
          nuevoEstado={transicionModal.nuevoEstado}
          loading={transicionLoading}
          error={transicionModal.error}
          hayCambios={sucio}
          onCancelar={() => !transicionLoading && setTransicionModal(null)}
          onConfirmar={ejecutarTransicion}
        />
      )}
    </div>
  );
}

const ICONO_TRANSICION = {
  Pausada: Pause,
  Completada: CheckCircle2,
  Cancelada: XCircle,
  Activa: Play,
};

function ModalTransicion({ nuevoEstado, loading, error, hayCambios: conCambios, onCancelar, onConfirmar }) {
  const meta = {
    Pausada: {
      titulo: 'Pausar campaña',
      cuerpo: 'Los usuarios no podrán reservar nuevas citas mientras esté pausada. Las citas ya reservadas se mantienen.',
      btn: 'Pausar',
    },
    Activa: {
      titulo: 'Reactivar campaña',
      cuerpo: 'Los usuarios podrán volver a reservar citas. Se revalidará la coherencia de los datos.',
      btn: 'Reactivar',
    },
    Completada: {
      titulo: 'Marcar campaña como completada',
      cuerpo: 'Esta acción no se puede deshacer. La campaña pasará a estado terminal.',
      btn: 'Marcar completada',
    },
    Cancelada: {
      titulo: 'Cancelar campaña',
      cuerpo: 'Esta acción no se puede deshacer. Todas las citas pendientes y reservadas se cancelarán. Los usuarios y técnicos serán notificados en una fase posterior.',
      btn: 'Cancelar campaña',
    },
  }[nuevoEstado];

  if (!meta) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 z-50 grid place-items-center px-4"
      onClick={loading ? undefined : onCancelar}
    >
      <div
        className="bg-white rounded-lg shadow-lg max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-semibold text-slate-900 mb-2">{meta.titulo}</h3>
        <p className="text-sm text-slate-600">{meta.cuerpo}</p>

        {/* Rediseño (Fase 2): cambiar de estado recarga la campaña; lo no guardado se pierde. */}
        {conCambios && (
          <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            Tienes cambios sin guardar: se perderán. Guárdalos antes si los quieres conservar.
          </p>
        )}

        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md p-2.5">
            {error}
          </div>
        )}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancelar}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          {/* Botón del DS (antes, negro a mano); cancelar la campaña, en rojo como antes. */}
          <Button
            type="button"
            size="sm"
            variant={nuevoEstado === 'Cancelada' ? 'destructive' : 'default'}
            onClick={onConfirmar}
            disabled={loading}
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {meta.btn}
          </Button>
        </div>
      </div>
    </div>
  );
}
